import { FC } from 'react';
import { FormProvider } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { useParams } from 'react-router-dom';
import { TFunction } from 'i18next';

import { SchemableFormNavigationContextProvider } from 'contexts/SchemableFormNavigationContext/SchemableFormNavigationContextProvider';
import { createHandle, createLoader } from 'services/breadcrumbs/breadcrumbs';
import { InvoiceDiscrepanciesServiceSingleton } from 'services/invoiceDiscrepancies';
import { useDiscrepancies } from 'services/invoiceDiscrepancies/hooks';
import { InvoiceDiscrepancy } from 'services/invoiceDiscrepancies/types';
import { useInvoiceVerificationDetail } from 'services/repositories/invoiceVerification/invoiceVerification';
import { useMessage } from 'services/repositories/messages/messages';
import { useCurrentPartner } from 'services/repositories/partners/partners';
import { DisplayModes } from 'services/webedi/types';
import { EMPTY_FIELD } from 'support/helpers/const/const';
import { routeToPage } from 'support/helpers/navigation/navigation';
import { RouteParams } from 'support/helpers/types/types';
import { DocumentType, InvoiceVerificationDTO, InvoiceVerificationRoutes, MessageV2DTO } from 'support/types';
import { Page } from 'components/Page/Page';
import { DocumentBaseSection } from 'components/WebEDI/DocumentBaseSection/DocumentBaseSection';

import { useFormValidationTrigger } from './hooks/useFormValidationTrigger';
import { useInvoiceValidation } from './hooks/useInvoiceValidation';
import { InvoiceVerificationDetailsSection, useUIConfig } from './hooks/useUIConfig';
import { DiscrepanciesBanner } from './components/DiscrepanciesBanner/DiscrepanciesBanner';
import { DiscrepanciesModal } from './components/DiscrepanciesModal/DiscrepanciesModal';
import { InvoiceVerificationActions } from './components/InvoiceVerificationActions/InvoiceVerificationActions';
import { InvoiceVerificationDetailsSidebar } from './components/InvoiceVerificationDetailsSidebar/InvoiceVerificationDetailsSidebar';
import { InvoiceVerificationDetailsSummary } from './components/InvoiceVerificationDetailsSummary/InvoiceVerificationDetailsSummary';
import { LineItemsSection } from './components/LineItemsSection/LineItemsSection';

export const InvoiceVerificationDetails = () => {
  const { invoiceId } = useParams<RouteParams<InvoiceVerificationRoutes.detail>>();
  const { data: invoiceVerificationDetails, isLoading: isLoadingInvoiceVerification } = useInvoiceVerificationDetail({
    variables: { id: invoiceId },
  });
  const { data: invoice, isLoading: isLoadingInvoice } = useMessage({
    variables: { messageId: invoiceVerificationDetails?.message.id },
  });

  return (
    <Page isLoading={isLoadingInvoice || isLoadingInvoiceVerification}>
      {invoiceVerificationDetails && invoice?.related_messages ? (
        <InvoiceVerificationDetailsUI
          invoiceVerificationDetails={invoiceVerificationDetails}
          relatedMessages={invoice?.related_messages}
          invoice={invoice?.data}
        />
      ) : null}
    </Page>
  );
};

type InvoiceVerificationDetailsUIProps = {
  invoiceVerificationDetails: InvoiceVerificationDTO;
  relatedMessages: Array<MessageV2DTO>;
  invoice: MessageV2DTO;
};

const InvoiceVerificationDetailsUI: FC<InvoiceVerificationDetailsUIProps> = ({
  invoiceVerificationDetails,
  relatedMessages,
  invoice,
}) => {
  const { data: currentPartner } = useCurrentPartner();
  const uiConfig = useUIConfig();
  const { formMethods } = useInvoiceValidation(invoiceVerificationDetails.payload);
  useFormValidationTrigger(formMethods, invoiceVerificationDetails.payload);

  return (
    <>
      <FormProvider {...formMethods}>
        <SchemableFormNavigationContextProvider<InvoiceDiscrepancy>
          service={InvoiceDiscrepanciesServiceSingleton}
          NavigableItemPopoverComponent={DiscrepanciesModal}
        >
          <Page.Head
            rightContent={
              <InvoiceVerificationActions invoice={invoice} invoiceVerification={invoiceVerificationDetails} />
            }
          ></Page.Head>

          <Page.Section
            topBanner={<DiscrepanciesBanner />}
            rightAside={
              <InvoiceVerificationDetailsSidebar
                document={invoice}
                relatedTransactions={relatedMessages}
                currentPartner={currentPartner}
                invoiceVerificationDetails={invoiceVerificationDetails}
              />
            }
          >
            <InvoiceVerificationDetailsSections config={uiConfig} />
          </Page.Section>
        </SchemableFormNavigationContextProvider>
      </FormProvider>
    </>
  );
};

const InvoiceVerificationDetailsSections = ({ config }: { config: Array<InvoiceVerificationDetailsSection> }) => {
  const { t } = useTranslation();
  useDiscrepancies(config);

  return (
    <div className="h-full *:pt-6 [&>*:first-child]:pt-0">
      <InvoiceVerificationDetailsSummary />
      {config?.map((config) => {
        switch (config.type) {
          case 'matched_line_items':
            return (
              <LineItemsSection
                dataPrefix="matched_line_items"
                key={config.type}
                mode={DisplayModes.view}
                config={config}
                documentType={DocumentType.invoice}
              />
            );

          case 'unmatched_line_items':
            return (
              <LineItemsSection
                dataPrefix="unmatched_line_items"
                key={config.type}
                mode={DisplayModes.view}
                config={config}
                documentType={DocumentType.invoice}
              />
            );
          default:
            return (
              <DocumentBaseSection
                key={config.type}
                mode={DisplayModes.view}
                isSubmitting={false}
                config={config}
                emptyState={t(`invoiceVerification:detailsPage.emptyState.${config.type}`)}
              />
            );
        }
      })}
    </div>
  );
};

/**
 * i18n keys
 * t('invoiceVerification:detailsPage.emptyState.allowances')
 * t('invoiceVerification:detailsPage.emptyState.charges')
 */

InvoiceVerificationDetails.loader = createLoader(
  ({ params }) => useInvoiceVerificationDetail.getKey({ id: params.invoiceId }),
  useInvoiceVerificationDetail.queryFn,
);

type ResponseType = Awaited<ReturnType<typeof useInvoiceVerificationDetail.queryFn>>;
InvoiceVerificationDetails.handle = createHandle<ResponseType, RouteParams<InvoiceVerificationRoutes.detail>>(
  (params, data) => ({
    label: (t: TFunction) =>
      t('invoiceVerification:detailsPage.breadcrumb', {
        externalRef: data?.message.external_message_ref || EMPTY_FIELD,
      }),
    to: routeToPage(InvoiceVerificationRoutes.detail, { invoiceId: params.invoiceId }),
    key: `invoiceVerification-${params.invoiceId}`,
  }),
);
